import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCompanyCapabilities } from '@/domain/companies/queries/capabilities';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';
import { useDocumentOcrTextMapPage } from '@/domain/documents/queries';
import { isUiExtractedFieldsHighlightedSelector } from '@/domain/ui/selectors';
import { displayedOcrPolygons } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/ocrGeometry';
import ExtractedFieldHighlights from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/ExtractedFieldHighlights';
import OcrWords from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords';
import StatementGrid from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/StatementGrid';

type Props = {
  documentId: string;
  pageNumber: number;
  width: number;
  height: number;
  scale: number;
  userRotation: number;
  showGrid: boolean;
  gridStartY: number;
  onCloseGrid: () => void;
};

const PageOcrLayer = ({
  documentId,
  pageNumber,
  width,
  height,
  scale,
  userRotation,
  showGrid,
  gridStartY,
  onCloseGrid,
}: Props) => {
  const { data: ocrPage } = useDocumentOcrTextMapPage(documentId, pageNumber);
  const companyId = useSelector(currentCompanyIdSelector);
  const { data: companyCapabilities } = useCompanyCapabilities(companyId);
  const isHighlightingEnabled = companyCapabilities?.polygon_highlighting.value === true;
  const isHighlighted = useSelector(isUiExtractedFieldsHighlightedSelector);
  const layout = useMemo(() => ({ width: width * scale, height, userRotation }), [width, scale, height, userRotation]);
  const displayedWords = useMemo(() => (ocrPage ? displayedOcrPolygons(ocrPage, layout) : []), [ocrPage, layout]);

  return (
    <>
      {isHighlightingEnabled && isHighlighted && ocrPage && Object.keys(ocrPage.extractedFields).length > 0 && (
        <ExtractedFieldHighlights ocrPage={ocrPage} layout={layout} />
      )}
      {ocrPage && ocrPage.words.length > 0 && (
        <OcrWords
          words={ocrPage.words}
          displayedWords={displayedWords}
          pageNumber={pageNumber}
          scale={scale}
          width={width}
          height={height}
        />
      )}
      {showGrid && (
        <StatementGrid
          mouseYPosition={gridStartY}
          handleGrid={onCloseGrid}
          width={width * scale}
          height={height}
          scale={scale}
        />
      )}
    </>
  );
};

export default PageOcrLayer;
